import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Printer, Download, ArrowLeft, LayoutDashboard, Package, Boxes,
  Inbox, KanbanSquare, Truck, Smartphone, MessageSquare, Megaphone, Building2,
  Users, Settings as SettingsIcon, Briefcase, Lock, ShieldCheck, FileText,
} from "lucide-react";

/**
 * Edenstone Ops — User Manual.
 *
 * Single-page, table-of-contents on the left, content on the right. The page
 * uses print-specific styling so the user can hit Print → Save as PDF and
 * end up with a clean printable document. Authenticated users see a "Back"
 * link to wherever they came from.
 */

const SECTIONS = [
  { id: "getting-started", icon: BookOpen,        title: "Getting started" },
  { id: "signing-in",      icon: Lock,            title: "Signing in" },
  { id: "dashboard",       icon: LayoutDashboard, title: "Dashboard" },
  { id: "inventory",       icon: Package,         title: "Inventory" },
  { id: "companies",       icon: Building2,       title: "Companies & contacts" },
  { id: "pipeline",        icon: KanbanSquare,    title: "Order Pipeline" },
  { id: "dockets",         icon: Truck,           title: "Dispatch Dockets" },
  { id: "yard-tablet",     icon: Smartphone,      title: "Yard Tablet" },
  { id: "po-inbox",        icon: Inbox,           title: "Purchase Orders inbox" },
  { id: "invoicing",       icon: FileText,        title: "Invoicing & Completion" },
  { id: "chat",            icon: MessageSquare,   title: "Internal chat" },
  { id: "marketing",       icon: Megaphone,       title: "Marketing" },
  { id: "portal",          icon: Briefcase,       title: "Reseller portal" },
  { id: "settings",        icon: SettingsIcon,    title: "Settings" },
  { id: "security",        icon: ShieldCheck,     title: "Security & roles" },
];

function Tip({ children }) {
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 my-3 flex gap-2 items-start">
      <span className="font-bold flex-shrink-0">Tip ·</span>
      <span>{children}</span>
    </div>
  );
}
function Note({ children }) {
  return (
    <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-700 my-3 flex gap-2 items-start">
      <span className="font-bold flex-shrink-0">Note ·</span>
      <span>{children}</span>
    </div>
  );
}
function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="manual-section scroll-mt-24 mb-12">
      <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-zinc-900 border-b border-zinc-200 pb-2 mb-4">
        <Icon className="w-5 h-5 text-brand-600" /> {title}
      </h2>
      <div className="text-sm text-zinc-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function UserManualPage() {
  const contentRef = useRef(null);

  // Active TOC highlight via IntersectionObserver — simple and JS-light.
  useEffect(() => {
    const tocLinks = document.querySelectorAll('[data-toc-link]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tocLinks.forEach((l) => l.classList.remove("toc-active"));
            const id = entry.target.getAttribute("id");
            const active = document.querySelector(`[data-toc-link="${id}"]`);
            if (active) active.classList.add("toc-active");
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    document.querySelectorAll(".manual-section").forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const printIt = () => window.print();

  return (
    <div className="min-h-screen bg-white">
      {/* Print-only stylesheet */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .manual-toc { display: none !important; }
          .manual-content { padding: 0 !important; max-width: 100% !important; }
          .manual-section { page-break-inside: avoid; }
          h2 { page-break-after: avoid; }
          body { font-size: 11pt; }
          a[href]::after { content: "" !important; }
        }
        .toc-active { color: rgb(180 83 9); font-weight: 600; background-color: rgb(254 243 199); }
      `}</style>

      {/* Top bar — hidden when printing */}
      <div className="no-print sticky top-0 z-30 bg-white border-b border-zinc-200 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
          <Link
            to="/"
            data-testid="manual-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex-1 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand-600" />
            <h1 className="font-display text-lg font-bold tracking-tight">Edenstone Ops — User Manual</h1>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-2">v1.0</span>
          </div>
          <button
            onClick={printIt}
            data-testid="manual-print"
            className="es-btn-secondary text-xs"
            title="Open the print dialog — choose 'Save as PDF' to download"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <button
            onClick={printIt}
            data-testid="manual-download-pdf"
            className="es-btn-primary text-xs"
            title="Use your browser's 'Save as PDF' destination"
          >
            <Download className="w-3.5 h-3.5" /> Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        {/* TOC */}
        <aside className="manual-toc no-print lg:sticky lg:top-20 self-start text-sm">
          <h3 className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-3">Contents</h3>
          <ul className="space-y-1">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-toc-link={s.id}
                  className="inline-flex items-center gap-2 px-2 py-1.5 rounded-md text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 w-full transition-colors"
                >
                  <s.icon className="w-3.5 h-3.5" /> {s.title}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* Manual content */}
        <article className="manual-content max-w-3xl" ref={contentRef}>
          <header className="mb-10 border-b border-zinc-200 pb-6">
            <div className="text-[10px] uppercase font-bold tracking-widest text-brand-700 mb-2">User Manual</div>
            <h1 className="font-display text-4xl font-black tracking-tight text-zinc-900 mb-2">Edenstone Masonry Ops</h1>
            <p className="text-sm text-zinc-500 max-w-xl">
              A 5-minute tour of the platform — what each page is for, the
              key buttons, and the day-to-day workflows your team will run.
            </p>
          </header>

          <Section id="getting-started" icon={BookOpen} title="Getting started">
            <p>
              Edenstone Ops is your single workspace for inventory, sales pipeline,
              dispatch dockets, reseller pricing, and customer communication.
              Everything is built around four big ideas:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Products & stock</strong> live in Inventory.</li>
              <li><strong>Customers</strong> live in Companies & Contacts.</li>
              <li><strong>Orders</strong> flow through the Pipeline → Dockets → Invoicing.</li>
              <li><strong>Resellers</strong> get their own self-service portal.</li>
            </ul>
            <Tip>Bookmark the dashboard — it's the highest-signal page in the app.</Tip>
          </Section>

          <Section id="signing-in" icon={Lock} title="Signing in">
            <p>Visit your Edenstone Ops URL. On the login page enter your email and password.</p>
            <p><strong>Forgot password?</strong> Use the link under the Sign-in button. You'll
              receive a reset link valid for one use.</p>
            <p><strong>First-time login.</strong> If your account was created by an admin, you'll
              be asked to set a new permanent password before you can do anything else.</p>
            <Note>The reseller portal lives at the same URL — log in with a reseller account and
              you'll be sent to /portal automatically.</Note>
          </Section>

          <Section id="dashboard" icon={LayoutDashboard} title="Dashboard">
            <p>The dashboard shows four KPIs at the top and three quick-access surfaces below:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Quick actions</strong> — open a New Order, Docket, Company, Contact, or Product modal without leaving the page.</li>
              <li><strong>Alerts</strong> — products below their stock minimum + overdue dockets. Click the low-stock card to jump straight to a filtered Inventory view.</li>
              <li><strong>Workflow stages</strong> — top 3 orders per stage; click to open the order.</li>
            </ul>
            <Tip>The "User Manual" button (top-right of the dashboard) brings you back to this page anytime.</Tip>
          </Section>

          <Section id="inventory" icon={Package} title="Inventory">
            <p>The Inventory page lists every product with: <em>SKU, Total stock, Pallets, Allocated, Available, Min warning, MSRP, Unit weight, Units / Pallet.</em></p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Inline edit</strong> Total / Allocated / Min warning — click the number, type, hit Enter or click away to save.</li>
              <li><strong>New / Edit / Delete</strong> products via the buttons on each row.</li>
              <li><strong>Bulk select</strong> using the checkboxes — then Edit or Delete many at once.</li>
              <li><strong>Import / Export</strong> CSV from the top right.</li>
              <li><strong>Columns</strong> — show/hide and reorder via the Columns button.</li>
            </ul>
            <Tip>An amber triangle next to a green "Available" number means the product has dropped to or below its Min warning. Replenish soon.</Tip>
          </Section>

          <Section id="companies" icon={Building2} title="Companies & contacts">
            <p>Every customer is a <strong>Company</strong>. Inside a company you can see its contacts, pricing, orders, dockets, and notes.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use <strong>Account Tier</strong> + <strong>Discount %</strong> to drive automatic reseller pricing.</li>
              <li>Toggle <strong>Portal Access</strong> to let a reseller log in to the self-service portal.</li>
              <li>Add <strong>Contacts</strong> linked to a company — each one has email, phone, mobile, and a job title.</li>
              <li><strong>Deleting</strong> a company cascades to its contacts, pricing rows, orders, and dockets — confirm carefully.</li>
            </ul>
          </Section>

          <Section id="pipeline" icon={KanbanSquare} title="Order Pipeline">
            <p>
              The Pipeline is a drag-and-drop kanban board with four columns:
              <strong> Orders In → Picking → To Invoice → Completed</strong>.
              Every stage transition writes an audit-log entry so you can trace
              who moved what, when, and from where.
            </p>
            <p><strong>How an order moves through it (step by step):</strong></p>
            <ol className="list-decimal pl-5 space-y-1.5">
              <li>
                <strong>Orders In</strong> — a new order lands here. Created
                either manually via Dashboard → "New Order" or automatically
                when an admin <em>Approves</em> a parsed PO in the PO Inbox.
                Optional new-order email notification is sent if it&apos;s turned
                on in <em>Settings → Notifications</em>.
              </li>
              <li>
                <strong>Picking</strong> — drag the card here when the yard starts
                allocating stock. Warehouse staff using the Yard Tablet see the
                docket and can update stock counts as they pick.
              </li>
              <li>
                <strong>To Invoice</strong> — picking is finished, awaiting
                invoicing. A sidebar badge + dashboard tile flag this so admin
                knows it&apos;s their turn. From here the order also shows up in the
                separate <em>Invoicing</em> kanban (<code>/invoicing</code>).
              </li>
              <li>
                <strong>Completed</strong> — invoice issued and the order is
                closed. Resellers see this as the final state in the portal.
                Two further internal states (<em>Invoiced, Paid</em>) exist for
                bookkeeping; they live on the Invoicing board, not on the
                Pipeline.
              </li>
            </ol>
            <p>
              <strong>The collapsible Upcoming Dockets panel</strong> above the
              kanban shows the next 6 pickups in deadline order. Overdue dockets
              are flagged red so dispatch can see them without leaving the
              page.
            </p>
            <p>
              <strong>Search &amp; declutter:</strong> the search bar in the
              header filters cards across every column by order number, customer
              name, company, or product. The <em>Completed</em> column also has
              a double-chevron toggle that collapses it down to a narrow vertical
              strip — useful once it gets noisy — and the preference is sticky
              across browser sessions.
            </p>
            <Tip>Admins can rename and reorder the stages from <em>Settings → Workflows & Statuses</em>. The canonical names don&apos;t change — only what your team sees.</Tip>
          </Section>

          <Section id="dockets" icon={Truck} title="Dispatch Dockets">
            <p>A docket records a pickup or delivery — quantity, deadline, delivery address, status (Pending → Scheduled → Picked Up).</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>New docket</strong> can be created from the Dockets page or from inside an order.</li>
              <li><strong>Confirm pickup</strong> marks the docket as Picked Up — also available on the Yard Tablet.</li>
              <li><strong>Print</strong> a docket PDF for the truck driver.</li>
            </ul>
          </Section>

          <Section id="yard-tablet" icon={Smartphone} title="Yard Tablet">
            <p>A finger-friendly tablet view designed for the warehouse. Two tabs:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Stock</strong> — chunky cards per SKU with ±1 / ±5 / ±10 / ±50 chips for Total Stock and Allocated.</li>
              <li><strong>Dockets</strong> — every pending pickup as a big card with a single <em>Confirm Pickup</em> button. Each docket card also has a <strong>printer icon</strong> at the top-right that opens the printable docket in a new tab (same view as <em>Dockets → Print</em>).</li>
            </ul>
            <Tip>Use the Warehouse role for staff on the yard — they can confirm pickups and update stock without seeing pricing or financial info.</Tip>
          </Section>

          <Section id="po-inbox" icon={Inbox} title="Purchase Orders inbox">
            <p>
              By default the inbox is fed by <strong>manual file upload</strong>
              (PDF / PNG / JPEG / WebP / DOCX / TXT) and <strong>pasted text</strong>.
              Gemini parses the document and extracts the customer, PO
              reference, delivery info, and line items.
            </p>
            <Note>
              <strong>Email-to-PO ingestion is OFF by default.</strong> When you&apos;re ready to
              auto-pull POs straight from a shared mailbox: first set up a dedicated email
              account (IMAP-enabled), then in <em>Settings → Email-to-PO ingestion</em> flip the
              master toggle <strong>On</strong> and fill in the IMAP credentials. Turning the toggle on
              brings back the <em>Refresh</em> button on this page, the sender/email labels on each
              PO, and the <em>“Original Email”</em> stage card. Flipping it off again hides all of
              that and suspends polling — no email-sourced POs will appear.
            </Note>
            <p><strong>Lifecycle of a PO:</strong></p>
            <ol className="list-decimal pl-5 space-y-1.5">
              <li>
                <strong>Awaiting Review</strong> — file arrives, Gemini hasn&apos;t
                touched it yet OR has just finished. Admin opens the document
                preview, edits any mis-parsed fields, then either Approves or
                Rejects.
              </li>
              <li>
                <strong>Parsing</strong> — transient state while Gemini works.
                The frontend polls in the background; you don&apos;t need to refresh.
              </li>
              <li>
                <strong>Approved</strong> — admin clicked Approve. One or more
                <em> orders</em> are created automatically in <em>Pipeline →
                Orders In</em>, mapped to the parsed customer and line items.
              </li>
              <li>
                <strong>Rejected</strong> — admin discarded the document
                (duplicate, spam, wrong supplier, etc.).
              </li>
              <li>
                <strong>Parse Failed</strong> — Gemini couldn&apos;t read it. Admin
                can re-trigger parsing or fill the fields manually.
              </li>
            </ol>
            <p><strong>Managing a growing inbox:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Review tab is the default view</strong> — only POs awaiting your attention.</li>
              <li><strong>Search</strong> by customer or PO reference (and sender email when email ingestion is on).</li>
              <li><strong>Date filter</strong> — Today / 7d / 30d / All time.</li>
              <li><strong>Archive</strong> — hide Approved / Rejected POs from the active inbox without deleting them. Toggle the Archive view to bring them back.</li>
              <li><strong>Bulk delete</strong> via the checkboxes for spam clean-up.</li>
            </ul>
            <Tip>Document preview supports zoom and shows a side-by-side line-item editor — useful when Gemini gets a quantity slightly wrong.</Tip>
            <p><strong>Live signal in the sidebar:</strong> the <em>Purchase Orders Inbox</em> nav item shows a red count badge whenever there are POs waiting for review. Badge auto-refreshes every 30 seconds so anyone walking past your screen can see new work has landed.</p>
            <p><strong>Parsing progress:</strong> when you upload a PDF or image, Gemini takes 6&ndash;25 seconds to read it. A progress panel inside the upload modal shows you which step is running (Uploading → Parsing → Saving) plus a live elapsed-time counter, so you know the app is doing something rather than frozen.</p>
          </Section>

          <Section id="invoicing" icon={FileText} title="Invoicing & Completion">
            <p>When an order moves to <strong>To Invoice</strong>, it pops onto the Invoicing kanban (<em>/invoicing</em>). Track it through Invoiced → Paid → Completed there. Internal statuses are kept off the reseller portal — they only see Completed.</p>
            <p>The <strong>Invoiced</strong> column has a double-chevron toggle that collapses it to a narrow vertical strip, mirroring the Pipeline behaviour. Handy when you want to focus on the &ldquo;To Invoice&rdquo; queue without the noise of historical orders. Preference is sticky across sessions.</p>
          </Section>

          <Section id="chat" icon={MessageSquare} title="Internal chat">
            <p>Side-bar chat with channels — like a lightweight Slack, scoped to your team. Soft-delete messages within 5 seconds, edit your own messages, and (as admin) rename or delete channels via the 3-dot menu.</p>
            <p><strong>How you know there&apos;s a new message:</strong> three layers, in order of intrusiveness, so you&apos;re not interrupted unless you choose to be.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Sidebar badge</strong> — red count next to <em>Internal Chat</em> while you have the app open. Auto-updates every 20 seconds.</li>
              <li><strong>Tab title + favicon</strong> — when the app is open in <em>any</em> browser tab the title becomes <code>(N) Edenstone Ops</code> and a red dot appears on the favicon. Glanceable even when the tab isn&apos;t focused. No setup required.</li>
              <li><strong>Desktop notifications</strong> — native macOS/Windows toast that pops even when you&apos;re on a different tab. Opt-in: go to <em>/profile</em> → <em>Desktop notifications</em> → <strong>Enable</strong>. Your browser will ask permission once. Toasts only fire when the app isn&apos;t already focused, so they&apos;ll never bug you when you&apos;re already looking at the chat.</li>
            </ul>
          </Section>

          <Section id="marketing" icon={Megaphone} title="Marketing">
            <p>
              Run promotional broadcasts to a list, a dynamic group (e.g. all
              <em> Wholesale-tier customers</em>), or a single company. Each
              campaign is bound to an <strong>Email Template</strong> with
              merge tags like <code>{"{{company.Company_Name}}"}</code> and
              <code>{" {{contact.First_Name}}"}</code>.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Build a <strong>Template</strong> (HTML body + subject, with merge-tag chips to insert at cursor).</li>
              <li>Create a <strong>List</strong> (static recipients) or a <strong>Group</strong> (rule-based, auto-refreshes).</li>
              <li>Compose a <strong>Campaign</strong>: pick template + recipients, optionally override subject.</li>
              <li>Click <strong>Send</strong> — opens the <strong>Safe Send</strong> modal (see below).</li>
              <li>Open the campaign to see per-recipient delivery status (Sent / Failed) and the rendered body that was actually sent.</li>
            </ul>
            <p><strong>Safe Send safeguards:</strong> the Send button never fires a campaign blind. It opens a pre-flight modal that shows:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Recipient count</strong> + <strong>SMTP status</strong> badges so you know what&apos;s about to happen.</li>
              <li>A <strong>rendered preview of the first recipient&apos;s email</strong> — exactly what they will see, with merge tags resolved and your personal signature appended.</li>
              <li><strong>Send to me only</strong> — fires a single test email to your own address so you can sanity-check formatting.</li>
              <li><strong>Type-to-confirm</strong> — you have to type the literal recipient count (e.g. <code>47</code>) into a field before the real Send button enables.</li>
            </ul>
            <Note>If SMTP isn&apos;t configured yet, recipients are still computed and stored as <em>pending</em> — they don&apos;t get re-sent automatically when you wire SMTP later, so make sure to hit Send again after configuring credentials.</Note>
          </Section>

          <Section id="profile" icon={SettingsIcon} title="My Profile & email signature">
            <p>
              Click your name/avatar at the bottom of the sidebar to open <em>/profile</em>.
              The profile page lets you set a <strong>personal email signature</strong> that gets
              stacked <strong>above</strong> the global Edenstone logo on every email <em>you</em> trigger
              (campaign sends, welcome emails, low-stock alerts, etc.).
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Signature is plain text, one detail per line — name, title, phone, etc.</li>
              <li>Updates take effect on your very next outgoing email — no restart required.</li>
              <li>Leave blank to use only the company logo (the previous behaviour).</li>
            </ul>
          </Section>

          <Section id="portal" icon={Briefcase} title="Reseller portal">
            <p>Resellers log into <em>/portal</em> and see only their own data: company info, inventory (stock-only by default), and their orders.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Inventory</strong> shows live stock levels. If the admin has enabled pricing, MSRP / Discount / Your Price / Line Total columns + the quote sidebar appear.</li>
              <li><strong>My Orders</strong> shows status as <em>Orders In / Picking / To Invoice / Completed</em>. Internal financial stages are never exposed.</li>
            </ul>
            <Note>Pricing visibility is toggled by an admin in <em>Settings → Reseller Portal</em>. Default is off until you're ready.</Note>
          </Section>

          <Section id="settings" icon={SettingsIcon} title="Settings">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Business</strong> — default discount %, default pickup lead days, default cost per extra pallet.</li>
              <li><strong>Workflows & Statuses</strong> — rename and reorder pipeline + docket stages.</li>
              <li><strong>Reseller Portal</strong> — global toggle to show or hide pricing across the portal.</li>
              <li><strong>Email</strong> — sender + low-stock recipient.</li>
              <li><strong>Notifications</strong> — low-stock + new-order alerts.</li>
              <li><strong>Email-to-PO ingestion</strong> — master toggle for auto-pulling POs from a mailbox via IMAP. <em>Off by default.</em> Requires a dedicated email account; turn on, then configure the IMAP credentials in the panel that appears.</li>
              <li><strong>Data maintenance</strong> — cleanup helpers (admin only).</li>
            </ul>
          </Section>

          <Section id="security" icon={ShieldCheck} title="Security & roles">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Admin</strong> — full access including Users, Settings, and Data maintenance.</li>
              <li><strong>Staff (user)</strong> — full operational access; cannot edit Settings or Users.</li>
              <li><strong>Warehouse</strong> — Yard Tablet + confirm pickups; no pricing or customer data.</li>
              <li><strong>Reseller</strong> — own portal only; no admin endpoints; cannot see other tenants&apos; data.</li>
            </ul>
            <p>Passwords are bcrypt-hashed. JWT tokens expire after the configured TTL. All admin-only API routes enforce role checks server-side, not just in the UI.</p>
          </Section>

          <footer className="text-[11px] text-zinc-400 font-mono pt-8 border-t border-zinc-100">
            Last updated: Feb 2026 · Edenstone Masonry Ops v1.0
          </footer>
        </article>
      </div>
    </div>
  );
}
